'use client';

import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, Zap, Thermometer, Moon, ArrowDown, Clock } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';

const warningSignsData = [
    {
        icon: Zap,
        titleKey: 'signs.warningSigns.severePain.title',
        descriptionKey: 'signs.warningSigns.severePain.description'
    },
    {
        icon: Thermometer,
        titleKey: 'signs.warningSigns.sensitivity.title',
        descriptionKey: 'signs.warningSigns.sensitivity.description'
    },
    {
        icon: AlertTriangle,
        titleKey: 'signs.warningSigns.swelling.title',
        descriptionKey: 'signs.warningSigns.swelling.description'
    },
    {
        icon: Moon,
        titleKey: 'signs.warningSigns.darkening.title',
        descriptionKey: 'signs.warningSigns.darkening.description'
    },
    {
        icon: ArrowDown,
        titleKey: 'signs.warningSigns.aching.title',
        descriptionKey: 'signs.warningSigns.aching.description'
    },
    {
        icon: Clock,
        titleKey: 'signs.warningSigns.prolonged.title',
        descriptionKey: 'signs.warningSigns.prolonged.description'
    }
];

export default function SignsSection() {
    const { t } = useTranslation('root-canal');
    const { isRTL } = useRTL();

    return (
        <section id="signs" className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900">
            <div className="container">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white mb-4 text-center font-serif">
                        <span className="text-gold">{t('signs.title.part1')}</span> {t('signs.title.part2')}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
                        {t('signs.subtitle')}
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {warningSignsData.map((sign, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1, duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <Card className="h-full border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 bg-white dark:bg-slate-800">
                                <CardContent className={`p-6 h-full flex flex-col ${isRTL ? 'text-right' : 'text-center'}`}>
                                    <div className={`w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gold/30 transition-all duration-300 ${isRTL ? 'mr-0 ml-auto' : 'mx-auto'}`}>
                                        <sign.icon className="w-8 h-8 text-gold" />
                                    </div>

                                    <h3 className={`text-xl font-bold text-gold dark:text-gold mb-4 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t(sign.titleKey)}
                                    </h3>

                                    <p className={`text-slate-600 dark:text-slate-300 leading-relaxed flex-1 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t(sign.descriptionKey)}
                                    </p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center mt-12"
                >
                    <Card className="max-w-4xl mx-auto bg-gold/10 border-gold/30 dark:border-gold/50">
                        <CardContent className="p-8">
                            <div className={`flex items-center justify-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <AlertTriangle className="w-6 h-6 text-gold" />
                                <h3 className="text-xl font-bold text-navy dark:text-white">
                                    {t('signs.emergency.title')}
                                </h3>
                            </div>
                            <p className={`text-navy dark:text-white leading-relaxed ${isRTL ? 'text-right' : 'text-center'}`}>
                                <strong>{t('signs.emergency.description.part1')}</strong> {t('signs.emergency.description.part2')}
                            </p>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </section>
    );
} 